import React from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { Text } from '@/components/common/text';
import Icon from 'react-native-vector-icons/Ionicons';
import { MOBILE_TYPOGRAPHY } from '@/constants/residents/mobile/mobileTypography';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useRadius } from '@/hooks/shared/useRadius';
import { useSpacing } from '@/hooks/shared/useSpacing';

interface CopyableInfoRowProps {
  /** Uppercase label text (e.g., "NETWORK NAME") */
  label: string;
  /** Value text to display */
  value: string;
  /** Handler when copy button is pressed */
  onCopy?: () => void;
  /** Handler when entire row is pressed */
  onPress?: () => void;
  /** Label text color (default: rgba(238, 241, 244, 0.5)) */
  labelColor?: string;
  /** Value text color (default: #EEF1F4) */
  valueColor?: string;
  /** Copy icon color (default: same as labelColor) */
  iconColor?: string;
  /** Copy icon size (default: 18) */
  iconSize?: number;
  /** Custom container style */
  style?: StyleProp<ViewStyle>;
}

/**
 * CopyableInfoRow
 * 
 * Reusable component for displaying a labeled value with optional copy functionality.
 * Matches Figma design for WiFi info, contact info, and other labeled data.
 * 
 * @example
 * // With copy button
 * <CopyableInfoRow
 *   label="NETWORK NAME"
 *   value="CBX wifi 101"
 *   onCopy={() => copyToClipboard(ssid)}
 * />
 * 
 * @example
 * // Clickable row without copy
 * <CopyableInfoRow
 *   label="EMAIL"
 *   value="support@example.com"
 *   onPress={() => openEmail(email)}
 * />
 */
export const CopyableInfoRow: React.FC<CopyableInfoRowProps> = ({
  label,
  value,
  onCopy,
  onPress,
  labelColor = 'rgba(238, 241, 244, 0.5)',
  valueColor = '#EEF1F4',
  iconColor,
  iconSize = 18,
  style,
}) => {
  const Container = onPress ? TouchableOpacity : View;
  const effectiveIconColor = iconColor || labelColor;
  const radius = useRadius();
  const space = useSpacing();
  // The copy glyph is bare: a padded box pulled back by the same margin gives
  // its hover tint a shape without moving the glyph (the hitSlop still reaches).
  const copyBox = { padding: space.xs, margin: -space.xs, borderRadius: radius.sm };

  return (
    <Container
      style={[styles.container, style]}
      onPress={onPress}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityLabel={`${label}: ${value}`}
    >
      <View style={styles.labelRow}>
        <Text style={[styles.label, { color: labelColor }]}>{label}</Text>
        {onCopy && (
          <Hoverable
            hover="row"
            style={copyBox}
            onPress={onCopy}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityRole="button"
            accessibilityLabel={`Copy ${label.toLowerCase()}`}
          >
            <Icon name="copy-outline" size={iconSize} color={effectiveIconColor} />
          </Hoverable>
        )}
      </View>
      <Text style={[styles.value, { color: valueColor }]}>{value}</Text>
    </Container>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 2,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  label: {
    ...MOBILE_TYPOGRAPHY.label,
  },
  value: {
    ...MOBILE_TYPOGRAPHY.body,
  },
});

export default CopyableInfoRow;
